import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { pushButtonClickEventToDataLayer } from '../../common/analytics.js';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables.js';
import { DialogType } from '../../common/enums.js';
import { downloadWarrantyReceiptPdf } from '../../common/fetch.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import { useStartFixer } from '../../common/hooks/useStartFixer';
import {
  activateSimCardMsg,
  activateSubMsg,
  changeOfDeviceMsg,
  downloadWarrantyReceiptMsg,
  editMsg,
  getLocale,
  orderNewMsg,
  redemptionMsg,
  supportRequestMsg,
  t,
  technicalErrorMsg,
  terminateSubscriptionMsg,
  terminationMsg,
  warrantyReceiptNotAvailableMsg,
} from '../../common/i18n/index.js';
import { CATEGORY_URL } from '../../common/utils/categoryUtils.js';
import { formatTimestampToUTCDDMMYYYY } from '../../common/utils/dateUtils.js';
import { type DownloadError, downloadFile } from '../../common/utils/domUtils.js';
import {
  CONSUMER_OMAELISA_LOGIN_URL,
  YTT_MANAGEMENT_SERVICE_URL,
  doActionIfNotPendingSubscription,
  getSubscriptionUrlId,
  isCommitmentPeriodActive,
  isHyllyLiittyma,
  isSubscriptionInSaunalahti,
  isSubscriptionInSfdc,
  isSubscriptionInTellusOrMipa,
  openSubscriptionDialog,
} from '../../common/utils/subscriptionUtils.js';
import { SubscriptionPbxDetails, SubscriptionStatusType, SubscriptionType } from '../../generated/api/models.js';
import { useConfig } from '../../public/site/ConfigProvider';
import { openDialog } from '../../public/site/dialogStore.js';
import {
  resumeSubscription,
  showSuspendSubscriptionOptions,
  showTerminateSubscriptionForm,
} from '../../selfservice/actions/index.js';
import { isNettiLiteSubscription } from './SubscriptionDetails.js';
import { isLiikkuvaWifiSubscription } from './subscriptionDetailsUtils.js';
import type { DialogParams } from '../../common/types/dialog.js';
import type { CategoryKey } from '../../common/utils/categoryUtils.js';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';
import type { Subscription } from '../../generated/api/models.js';

export enum SubscriptionDetailsLinkType {
  SUPPORT_REQUEST,
  TEMPORARILY_CLOSE,
  TERMINATE_NETTILITE_VIA_DIALOG,
  TERMINATE,
  RESUME_SUBSCRIPTION,
  EPP_TERMINATE,
  EPP_REDEEM,
  EPP_DAMAGE_OR_SUPPORT_REQUEST,
  EPP_DEVICE_CHANGE,
  CHANGE_OFFER,
  CHANGE_OFFER_PENDING_ACTIONS,
  BROADBAND_FIXER,
  MOBILE_FIXER,
  CHANGE_NETTI_LITE_SUBSCRIPTION,
  UPDATE_LIIKKUVA_WIFI_SUBSCRIPTION,
  EDIT_IN_CLASSIC,
  EDIT_IN_SAUNALAHTI,
  ORDER_SIM,
  ACTIVATE_SIM,
  DOWNLOAD_WARRANTY_RECEIPT,
  YTT_MANAGEMENT_SERVICE,
}

interface LinkData {
  type: SubscriptionDetailsLinkType;
  icon: string;
  label: string;
  url?: string;
  onClickCallback?: () => void;
  openLinkInNewTab?: boolean;
  hidden?: boolean;
  arrow?: boolean;
  iconOnRight?: string;
}

interface SubscriptionDetailsLinkBlocksProps {
  linkTypes: SubscriptionDetailsLinkType[];
  companyInfo?: CompanyInfoResponse;
  portal?: 'NOE' | 'EOE';
  category: CategoryKey;
  subscription: Subscription;
  hasPendingActions: boolean;
  setShowEppRedeem?: (showEppRedeem: boolean) => void;
  setShowTerminateNettiLite?: (showTerminateNettilite: boolean) => void;
}

const getUpdateLinkType = (subscription: Subscription, changeOfferAllowed?: boolean) => {
  const allowedSubscriptionStatuses = [SubscriptionStatusType.ACTIVE, SubscriptionStatusType.SUSPENDED, undefined];
  if (changeOfferAllowed && isNettiLiteSubscription(subscription?.subscriptionName)) {
    return SubscriptionDetailsLinkType.CHANGE_NETTI_LITE_SUBSCRIPTION;
  }
  if (isLiikkuvaWifiSubscription(subscription?.subscriptionName)) {
    return SubscriptionDetailsLinkType.UPDATE_LIIKKUVA_WIFI_SUBSCRIPTION;
  }
  if (allowedSubscriptionStatuses.includes(subscription!.subscriptionStatus) && changeOfferAllowed) {
    return SubscriptionDetailsLinkType.CHANGE_OFFER;
  }
  return SubscriptionDetailsLinkType.CHANGE_OFFER_PENDING_ACTIONS;
};

export const getVoiceLinkTypes = (subscription: Subscription, changeOfferAllowed: boolean) => {
  if (isSubscriptionInSaunalahti(subscription)) {
    return [SubscriptionDetailsLinkType.EDIT_IN_SAUNALAHTI];
  }
  if (isSubscriptionInTellusOrMipa(subscription)) {
    return [SubscriptionDetailsLinkType.EDIT_IN_CLASSIC, SubscriptionDetailsLinkType.SUPPORT_REQUEST];
  }
  const isSuspended = subscription?.subscriptionStatus === SubscriptionStatusType.SUSPENDED;
  const linkTypes = [getUpdateLinkType(subscription!, changeOfferAllowed)];
  if (isSuspended) {
    linkTypes.push(SubscriptionDetailsLinkType.RESUME_SUBSCRIPTION);
  } else if (!isHyllyLiittyma(subscription) && subscription.subscriptionStatus === SubscriptionStatusType.ACTIVE) {
    linkTypes.push(SubscriptionDetailsLinkType.TEMPORARILY_CLOSE);
  }
  linkTypes.push(SubscriptionDetailsLinkType.SUPPORT_REQUEST);
  if (subscription.subscriptionStatus === SubscriptionStatusType.ACTIVE) {
    linkTypes.push(SubscriptionDetailsLinkType.TERMINATE);
  }

  if (
    subscription?.subscriptionType === SubscriptionType.MOBILE &&
    subscription.subscriptionStatus === SubscriptionStatusType.ACTIVE
  ) {
    linkTypes.push(SubscriptionDetailsLinkType.MOBILE_FIXER);
  }

  const pbxType = subscription.details?.specialNumber?.pbxConfigurationDetails?.pbxType;
  if (pbxType !== undefined) {
    const noResume = [
      SubscriptionPbxDetails.PbxTypeEnum.RYHMAKUTSU,
      SubscriptionPbxDetails.PbxTypeEnum.VASTAUSSARJA,
      SubscriptionPbxDetails.PbxTypeEnum.TAVOITETTAVUUSKETJU,
    ];
    return noResume.includes(pbxType) ? linkTypes.slice(2) : linkTypes.slice(1);
  }
  return linkTypes;
};

export const getBroadbandLinkTypes = (subscription: Subscription, changeOfferAllowed: boolean) => {
  if (isSubscriptionInSaunalahti(subscription)) {
    return [SubscriptionDetailsLinkType.EDIT_IN_SAUNALAHTI];
  }
  if (isSubscriptionInTellusOrMipa(subscription)) {
    return [SubscriptionDetailsLinkType.EDIT_IN_CLASSIC, SubscriptionDetailsLinkType.SUPPORT_REQUEST];
  }

  const linkTypes = [];
  if (
    subscription.subscriptionType === SubscriptionType.MOBILE_BROADBAND ||
    subscription.subscriptionType === SubscriptionType.MOBILE_M2M
  ) {
    linkTypes.push(getUpdateLinkType(subscription!, changeOfferAllowed));
    linkTypes.push(SubscriptionDetailsLinkType.SUPPORT_REQUEST);
    if (subscription.subscriptionStatus === SubscriptionStatusType.SUSPENDED) {
      linkTypes.push(SubscriptionDetailsLinkType.RESUME_SUBSCRIPTION);
    } else if (!isHyllyLiittyma(subscription) && subscription.subscriptionStatus === SubscriptionStatusType.ACTIVE) {
      linkTypes.push(SubscriptionDetailsLinkType.TEMPORARILY_CLOSE);
    }
    if (subscription.subscriptionStatus === SubscriptionStatusType.ACTIVE) {
      linkTypes.push(SubscriptionDetailsLinkType.TERMINATE);
    }

    if (
      isSubscriptionInSfdc(subscription) &&
      subscription.subscriptionType === SubscriptionType.MOBILE_BROADBAND &&
      subscription.subscriptionStatus === SubscriptionStatusType.ACTIVE
    ) {
      linkTypes.push(SubscriptionDetailsLinkType.MOBILE_FIXER);
    }
  } else {
    linkTypes.push(SubscriptionDetailsLinkType.SUPPORT_REQUEST);
    linkTypes.push(SubscriptionDetailsLinkType.TERMINATE_NETTILITE_VIA_DIALOG);
    if (subscription.subscriptionStatus === SubscriptionStatusType.ACTIVE) {
      linkTypes.push(SubscriptionDetailsLinkType.BROADBAND_FIXER);
    }
  }
  return linkTypes;
};

export const SubscriptionDetailsLinkBlocks = ({
  linkTypes,
  companyInfo,
  portal = 'NOE',
  subscription,
  hasPendingActions,
  category,
  setShowEppRedeem,
  setShowTerminateNettiLite,
}: SubscriptionDetailsLinkBlocksProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [downloadError, setDownloadError] = useState<DownloadError>(false);

  const downloadWarrantyReceipt = async () => {
    setDownloadError(false);
    const error = await downloadFile(
      () => downloadWarrantyReceiptPdf(subscription.deliveryOrderId!),
      `warranty-receipt-${subscription.deliveryOrderId}.pdf`
    );
    setDownloadError(error);
  };
  const startFixer = useStartFixer();
  const { search } = useLocation();
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const { classicSiteUrl: classicBaseUrl } = useConfig();
  const classicEditUrl = subscription.subscriptionUserFriendlyId
    ? `${classicBaseUrl}/muutos-ja-poisto/msisdn/${subscription.subscriptionUserFriendlyId}`
    : `${classicBaseUrl}/muutos-ja-poisto/`;
  const subscriptionId = subscription.subscriptionId;
  const subscriptionUrlId = getSubscriptionUrlId(subscription);

  const onShowDialog = (params: DialogParams) => openDialog(params);
  const generateChangeOfferPath = () => {
    const redirectPath = (() => {
      switch (subscription.subscriptionType) {
        case SubscriptionType.MOBILE_M2M:
          return paths.PS_BROADBAND_SUBSCRIPTION_UPDATE_M2M + search;
        case SubscriptionType.MOBILE_BROADBAND:
          return paths.PS_BROADBAND_SUBSCRIPTION_UPDATE + search;
        default:
          return paths.PS_MOBILE_SUBSCRIPTION_UPDATE + search;
      }
    })();
    return generatePath(redirectPath, { subscriptionId: subscriptionUrlId });
  };

  const availableLinkData: LinkData[] = [
    {
      type: SubscriptionDetailsLinkType.EPP_DAMAGE_OR_SUPPORT_REQUEST,
      label: t.N2CC('Device maintenance request'),
      icon: 'maintenance',
      url:
        portal === 'NOE'
          ? generatePath(paths.PS_DEVICE_SUPPORT_REQUEST + search, {
              subscriptionId: subscriptionUrlId,
            })
          : generatePath(paths.EMPLOYEE_DEVICE_SUPPORT_REQUEST, { subscriptionId }),
    },
    {
      type: SubscriptionDetailsLinkType.EPP_REDEEM,
      label: t.B9EG(redemptionMsg),
      icon: 'invoice',
      onClickCallback: hasPendingActions
        ? () => onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING })
        : () => {
            if (portal !== 'NOE' && setShowEppRedeem) {
              setShowEppRedeem(true);
            }
          },
      url:
        !hasPendingActions && portal === 'NOE'
          ? generatePath(paths.PS_DEVICE_REDEMPTION + search, {
              subscriptionId: subscriptionUrlId,
            })
          : undefined,
    },
    {
      type: SubscriptionDetailsLinkType.EPP_TERMINATE,
      label: t.D1IS(terminationMsg),
      icon: 'block',
      onClickCallback: () => {
        openSubscriptionDialog(
          onShowDialog,
          {
            subscription,
            companyInfo,
            type: DialogType.EPP_TERMINATE,
          },
          hasPendingActions
        );
      },
    },
    {
      type: SubscriptionDetailsLinkType.RESUME_SUBSCRIPTION,
      label: t.QV5K(activateSubMsg),
      icon: 'block',
      onClickCallback: () => {
        doActionIfNotPendingSubscription(
          onShowDialog,
          () => dispatch(resumeSubscription(subscriptionId)),
          hasPendingActions
        );
      },
    },
    {
      type: SubscriptionDetailsLinkType.SUPPORT_REQUEST,
      label: t.ROBH(supportRequestMsg),
      icon: 'maintenance',
      onClickCallback: () => {
        openSubscriptionDialog(
          onShowDialog,
          {
            type: DialogType.SUBSCRIPTION_SUPPORT_REQUEST,
            mdmId,
          },
          hasPendingActions
        );
      },
    },
    {
      type: SubscriptionDetailsLinkType.TEMPORARILY_CLOSE,
      label: t.IATD('Close temporarily'),
      icon: 'block',
      onClickCallback: () => {
        doActionIfNotPendingSubscription(
          onShowDialog,
          () => dispatch(showSuspendSubscriptionOptions()),
          hasPendingActions
        );
      },
    },
    {
      type: SubscriptionDetailsLinkType.TERMINATE,
      label: t.M1TW(terminateSubscriptionMsg),
      icon: 'power',
      onClickCallback: () => {
        if (isCommitmentPeriodActive(subscription.details?.commitmentEndDate)) {
          onShowDialog({
            type: DialogType.SUBSCRIPTION_TERMINATION_FORBIDDEN,
            commitmentEndDate: formatTimestampToUTCDDMMYYYY(subscription.details?.commitmentEndDate)!,
          });
        } else {
          doActionIfNotPendingSubscription(
            onShowDialog,
            () => dispatch(showTerminateSubscriptionForm()),
            hasPendingActions
          );
        }
      },
    },
    {
      type: SubscriptionDetailsLinkType.TERMINATE_NETTILITE_VIA_DIALOG,
      label: t.M1TW(terminateSubscriptionMsg),
      icon: 'power',
      onClickCallback: () => {
        if (hasPendingActions) {
          onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
        } else if (setShowTerminateNettiLite) {
          setShowTerminateNettiLite(true);
        }
      },
    },
    {
      type: SubscriptionDetailsLinkType.CHANGE_OFFER,
      label: t.RWLD('Update subscription'),
      icon: 'edit',
      onClickCallback: hasPendingActions
        ? () => onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING })
        : undefined,
      url: !hasPendingActions ? generateChangeOfferPath() : undefined,
    },
    {
      type: SubscriptionDetailsLinkType.CHANGE_NETTI_LITE_SUBSCRIPTION,
      label: t.RWLD('Update subscription'),
      icon: 'edit',
      onClickCallback: () => {
        openSubscriptionDialog(
          onShowDialog,
          {
            type: DialogType.CHANGE_NETTI_LITE_SUBSCRIPTION,
          },
          hasPendingActions
        );
      },
    },
    {
      type: SubscriptionDetailsLinkType.CHANGE_OFFER_PENDING_ACTIONS,
      label: t.RWLD('Update subscription'),
      icon: 'edit',
      onClickCallback: () => {
        openSubscriptionDialog(
          onShowDialog,
          {
            type: DialogType.SUBSCRIPTION_ACTION_PENDING,
          },
          hasPendingActions
        );
      },
    },
    {
      type: SubscriptionDetailsLinkType.EPP_DEVICE_CHANGE,
      label: t.ZHBT(changeOfDeviceMsg),
      icon: 'update',
      onClickCallback: () => {
        openSubscriptionDialog(
          onShowDialog,
          {
            type: DialogType.EPP_DEVICE_CHANGE,
            subscription: subscription,
          },
          hasPendingActions
        );
      },
    },
    {
      type: SubscriptionDetailsLinkType.BROADBAND_FIXER,
      label: t.OTF5('Connection problems?'),
      icon: 'wifi',
      onClickCallback: () => startFixer(subscription, getLocale(), portal),
    },
    {
      type: SubscriptionDetailsLinkType.MOBILE_FIXER,
      label: t.OTF5('Connection problems?'),
      icon: 'wifi',
      onClickCallback: () => startFixer(subscription, getLocale(), portal),
    },
    {
      type: SubscriptionDetailsLinkType.UPDATE_LIIKKUVA_WIFI_SUBSCRIPTION,
      label: t.RWLD('Update subscription'),
      icon: 'edit',
      onClickCallback: () => {
        openSubscriptionDialog(
          onShowDialog,
          {
            type: DialogType.UPDATE_LIIKKUVA_WIFI_SUBSCRIPTION,
          },
          hasPendingActions
        );
      },
    },
    {
      type: SubscriptionDetailsLinkType.EDIT_IN_CLASSIC,
      label: t.NVPK(editMsg),
      icon: 'external-link',
      url: classicEditUrl,
      openLinkInNewTab: true,
    },
    {
      type: SubscriptionDetailsLinkType.EDIT_IN_SAUNALAHTI,
      label: t.NVPK(editMsg),
      icon: 'external-link',
      url: CONSUMER_OMAELISA_LOGIN_URL,
      openLinkInNewTab: true,
    },
    {
      type: SubscriptionDetailsLinkType.ORDER_SIM,
      label: t.PMFC(orderNewMsg),
      icon: 'sim-add',
      url: `${paths.PS_HOME}/${CATEGORY_URL[category]}/${subscriptionUrlId}/tilaa-sim${search}`,
    },
    {
      type: SubscriptionDetailsLinkType.ACTIVATE_SIM,
      label: t.M1E7(activateSimCardMsg),
      icon: 'sim',
      url: `${paths.PS_HOME}/${CATEGORY_URL[category]}/${subscriptionUrlId}/aktivoi-sim-kortti${search}`,
    },
    {
      type: SubscriptionDetailsLinkType.DOWNLOAD_WARRANTY_RECEIPT,
      label: t.IB0X(downloadWarrantyReceiptMsg),
      icon: 'file-general',
      onClickCallback: () => void downloadWarrantyReceipt(),
      hidden: !subscription.deliveryOrderId || subscription.details?.device?.eppSubscription === true,
      arrow: false,
      iconOnRight: 'download',
    },
    {
      type: SubscriptionDetailsLinkType.YTT_MANAGEMENT_SERVICE,
      label: t.QJL0('Move to the installation and management service'),
      icon: 'external-link',
      url: YTT_MANAGEMENT_SERVICE_URL,
      openLinkInNewTab: true,
    },
  ];

  const links = linkTypes.map(
    linkType => availableLinkData.find(element => element.type === linkType) ?? ({} as LinkData)
  );

  const visibleLinks = links.filter(linkData => !linkData.hidden);

  const renderLinkBlock = (linkData: LinkData, index: number) => {
    let border: 'bottom' | 'top' | 'top-bottom';
    if (index === 0) {
      border = 'bottom';
    } else if (index === visibleLinks.length - 1) {
      border = 'top';
    } else {
      border = 'top-bottom';
    }
    const onClick = () => {
      pushButtonClickEventToDataLayer(SubscriptionDetailsLinkType[linkData.type].toLowerCase(), linkData.url);
      if (linkData.onClickCallback) {
        linkData.onClickCallback();
      }
      if (linkData.url) {
        if (linkData.openLinkInNewTab) {
          window.open(linkData.url, '_blank');
        } else {
          navigate(linkData.url);
        }
      }
    };
    return (
      <CL.LinkBlock
        key={linkData.type}
        className={`${SubscriptionDetailsLinkType[linkData.type].toLowerCase()} ${visibleLinks.length === 1 ? dsClass.BORDER_STYLE_NONE : ''}`}
        onClick={onClick}
        icon={<CL.Icon icon={linkData.icon} />}
        border={border}
        arrow={linkData.arrow}
      >
        <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_SPACE_BETWEEN}`}>
          <h4>{linkData.label}</h4>
          {linkData.iconOnRight && <CL.Icon icon={linkData.iconOnRight} type="filled" />}
        </div>
      </CL.LinkBlock>
    );
  };

  return (
    <>
      {visibleLinks.map((linkData, index) => renderLinkBlock(linkData, index))}
      {downloadError && (
        <CL.Notification
          className={`${dsClass.MARGIN_TOP_2} ${dsClass.MARGIN_BOTTOM_0}`}
          type="error"
          text={downloadError === 'notFound' ? t.Q8W5(warrantyReceiptNotAvailableMsg) : t.T4OU(technicalErrorMsg)}
          visible={true}
        />
      )}
    </>
  );
};
